// src/components/partner/EditTeamMemberModal.jsx
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Save,
  X,
} from "lucide-react";
import { updatePartnerTeamMember } from "../../lib/partner/partnerTeam";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  cohort_manager:
    "Cohort manager",
  facilitator: "Facilitator",
  viewer: "Viewer",
};

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  suspended: "Suspended",
};

export default function EditTeamMemberModal({
  open,
  member,
  actorRole,
  onClose,
  onUpdated,
}) {
  const [role, setRole] =
    useState("viewer");

  const [status, setStatus] =
    useState("active");

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const availableRoles =
  useMemo(() => {
    if (actorRole === "owner") {
      return [
        "owner",
        "admin",
        "cohort_manager",
        "facilitator",
        "viewer",
      ];
    }

    return [
      "admin",
      "cohort_manager",
      "facilitator",
      "viewer",
    ];
  }, [actorRole]);

  useEffect(() => {
    if (!open || !member) {
      return undefined;
    }

    setRole(
      member.role || "viewer"
    );

    setStatus(
      member.status || "active"
    );

    setSaving(false);
    setError("");

    function handleEscape(event) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    }

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    member,
    saving,
    onClose,
  ]);

  if (!open || !member) {
    return null;
  }

  function handleBackdropClick(
    event
  ) {
    if (
      event.target ===
        event.currentTarget &&
      !saving
    ) {
      onClose?.();
    }
  }

  async function handleSubmit(
    event
  ) {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");

      await updatePartnerTeamMember({
        partnerMemberId:
          member.partner_member_id,

        role,
        status,
      });

      await onUpdated?.({
        member,
        role,
        status,
      });

      onClose?.();
    } catch (submitError) {
      console.error(
        "Updating partner team member failed:",
        submitError
      );

      setError(
        submitError?.message ||
          "The team member could not be updated."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="presentation"
      onMouseDown={
        handleBackdropClick
      }
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-team-member-title"
        className="w-full rounded-t-[28px] bg-white shadow-2xl sm:max-w-lg sm:rounded-[28px]"
      >
        <header className="flex items-start justify-between border-b border-[#EFEFEF] px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF914D]">
              Partner team
            </p>

            <h2
              id="edit-team-member-title"
              className="mt-1 text-2xl font-black text-[#333333]"
            >
              Edit team member
            </h2>

            <p className="mt-2 text-sm text-[#757575]">
              {member.full_name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close edit team member form"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-6"
        >
          {error && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="edit-team-role"
              className="block text-sm font-black text-[#333333]"
            >
              Partner role
            </label>

            <select
              id="edit-team-role"
              value={role}
              onChange={(event) =>
                setRole(
                  event.target.value
                )
              }
              disabled={saving}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              {availableRoles.map(
                (roleOption) => (
                  <option
                    key={roleOption}
                    value={roleOption}
                  >
                    {
                      ROLE_LABELS[
                        roleOption
                      ]
                    }
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label
              htmlFor="edit-team-status"
              className="block text-sm font-black text-[#333333]"
            >
              Access status
            </label>

            <select
              id="edit-team-status"
              value={status}
              onChange={(event) =>
                setStatus(
                  event.target.value
                )
              }
              disabled={saving}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              {Object.entries(
                STATUS_LABELS
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                )
              )}
            </select>

            <p className="mt-2 text-xs leading-relaxed text-[#757575]">
              Suspended and inactive
              members cannot enter the
              Partner Portal.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="mr-2 h-4 w-4" />

              {saving
                ? "Saving changes…"
                : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}